import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getSubmissions, gradeSubmission, getCourse, downloadSubmission } from '../../api/instructorApi';
import Toast from '../../components/admin/Toast';

const AV_COLORS = ['#E8440A', '#F7C91B', '#1A7048', '#5B6ABF', '#D4882A', '#8A6FB0'];
function initials(name = '') { return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase(); }
function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function InstructorAssignmentSubs() {
  const { courseId, assignmentId } = useParams();
  const navigate = useNavigate();

  const [course,      setCourse]      = useState(null);
  const [assignment,  setAssignment]  = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [stats,       setStats]       = useState(null);
  const [toast,       setToast]       = useState(null);
  const [gradeModal,  setGradeModal]  = useState(null); // { submission }
  const [gradeForm,   setGradeForm]   = useState({ grade: '', feedback: '' });
  const [saving,      setSaving]      = useState(false);

  const load = useCallback(() => {
    getCourse(Number(courseId)).then(d => setCourse(d.course)).catch(console.error);
    getSubmissions(Number(courseId), Number(assignmentId)).then(d => {
      setAssignment(d.assignment);
      setSubmissions(d.submissions);
      setStats(d.stats);
    }).catch(console.error);
  }, [courseId, assignmentId]);

  useEffect(() => { load(); }, [load]);

  async function handleGrade(e) {
    e.preventDefault();
    if (!gradeModal) return;
    setSaving(true);
    try {
      await gradeSubmission(Number(courseId), Number(assignmentId), gradeModal.submission_id, gradeForm);
      load();
      setGradeModal(null);
      setToast({ msg: 'Grade saved', type: 'success' });
    } catch (err) {
      setToast({ msg: err.message, type: 'error' });
    } finally { setSaving(false); }
  }

  if (!assignment || !course) return <div className="body" style={{ color: 'var(--muted)' }}>Loading…</div>;

  const subCount     = stats?.submitted      ?? 0;
  const gradedCount  = stats?.graded         ?? 0;
  const totalStudents = stats?.total_students ?? 0;

  return (
    <>
      {/* ── Header ── */}
      <div className="ph">
        <div>
          <div className="bc">Instructor / {course.title} / Assignments / Submissions</div>
          <div className="pt">{assignment.title}</div>
          <div className="ps">
            Due {fmtDate(assignment.due_date)} · {subCount} submission{subCount !== 1 ? 's' : ''}
          </div>
        </div>
        <button
          className="btn btn-o btn-sm"
          onClick={() => navigate(`/instructor/courses/${courseId}?tab=assignments`)}
        >
          ← Back to Assignments
        </button>
      </div>

      <div className="body">
        {/* Stats */}
        <div className="stats" style={{ gridTemplateColumns: 'repeat(3,1fr)' }}>
          <div className="sc">
            <div className="sc-l">Submissions</div>
            <div className="sc-v">{subCount} / {totalStudents}</div>
            {totalStudents > 0 && (
              <div className="sc-bar">
                <div className="sc-fill" style={{ width: `${Math.round(subCount / totalStudents * 100)}%`, background: 'var(--yellow)' }} />
              </div>
            )}
          </div>
          <div className="sc">
            <div className="sc-l">Graded</div>
            <div className="sc-v">{gradedCount} / {subCount}</div>
            {subCount > 0 && (
              <div className="sc-bar">
                <div className="sc-fill" style={{ width: `${Math.round(gradedCount / subCount * 100)}%`, background: 'var(--green)' }} />
              </div>
            )}
          </div>
          <div className="sc">
            <div className="sc-l">Max Score</div>
            <div className="sc-v">{assignment.max_score} pts</div>
          </div>
        </div>

        {/* Table */}
        <div className="card">
          <div className="ct">
            <span>Student Submissions</span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Status</th>
                  <th>File</th>
                  <th>Submitted</th>
                  <th>Grade</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {submissions.map((sub, i) => (
                  <tr key={sub.student_id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                        <div style={{
                          width: 26, height: 26, borderRadius: '50%',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontFamily: "'Nunito Sans',sans-serif", fontSize: 9, fontWeight: 700,
                          background: AV_COLORS[i % 6],
                          color: i === 1 ? 'var(--black)' : 'white',
                        }}>
                          {initials(sub.student_name)}
                        </div>
                        {sub.student_name}
                      </div>
                    </td>
                    <td>
                      <span className={`b ${sub.submitted ? 'b-g' : 'b-gr'}`}>
                        {sub.submitted ? 'Submitted' : 'Not Submitted'}
                      </span>
                    </td>
                    <td style={{ fontSize: 12.5, color: 'var(--muted)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {sub.file_name || '—'}
                        {sub.viewed && <span className="b b-g" style={{ fontSize: 10 }}>Viewed</span>}
                      </div>
                    </td>
                    <td style={{ fontSize: 12.5, color: 'var(--muted)', fontFamily: "'DM Mono',monospace" }}>
                      {fmtDate(sub.submitted_at)}
                    </td>
                    <td>
                      {sub.grade ? (
                        <span className="b b-g">{sub.grade}</span>
                      ) : sub.submitted ? (
                        <button
                          className="btn btn-g btn-sm"
                          onClick={() => {
                            setGradeForm({ grade: '', feedback: '' });
                            setGradeModal(sub);
                          }}
                        >
                          Grade
                        </button>
                      ) : (
                        <span style={{ color: 'var(--muted)', fontSize: 12 }}>—</span>
                      )}
                    </td>
                    <td>
                      {sub.submitted && sub.submission_id && (
                        <button
                          className="btn btn-o btn-sm"
                          onClick={() => downloadSubmission(courseId, assignmentId, sub.submission_id, sub.file_name)}
                        >
                          ↓ Download
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ── Grade Modal ── */}
      {gradeModal && (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-title">
              Grade Submission — {gradeModal.student_name}
              <button className="btn btn-o btn-sm" onClick={() => setGradeModal(null)}>✕</button>
            </div>
            <form onSubmit={handleGrade}>
              <div className="fg">
                <label className="fl">Grade</label>
                <input className="li" required placeholder="e.g. A, B+, 85"
                  value={gradeForm.grade}
                  onChange={e => setGradeForm(f => ({ ...f, grade: e.target.value }))} />
              </div>
              <div className="fg" style={{ marginBottom: 0 }}>
                <label className="fl">Feedback <span style={{ color: 'var(--subtle)', fontWeight: 400 }}>(optional)</span></label>
                <textarea className="li" placeholder="Write feedback for the student…"
                  value={gradeForm.feedback}
                  onChange={e => setGradeForm(f => ({ ...f, feedback: e.target.value }))} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
                <button type="button" className="btn btn-o" onClick={() => setGradeModal(null)}>Cancel</button>
                <button type="submit" className="btn btn-g" disabled={saving}>
                  {saving ? 'Saving…' : 'Save Grade'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {toast && (
        <Toast message={toast.msg} type={toast.type} onDone={() => setToast(null)} />
      )}
    </>
  );
}
